import { type EditAreaTemplate, MAX_EDIT_AREA_TEMPLATE_NAME_LENGTH } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import {
  useEditAreaTemplatesQuery,
  useInvalidateEditAreaTemplates,
} from '@/entities/edit-area-template';
import {
  EditAreaTemplateRow,
  TemplateFromProductModal,
  useDeleteEditAreaTemplate,
  useUpdateEditAreaTemplate,
} from '@/features/edit-area/templates';
import { Button, ContentCard, cn, EmptyState, NameFormModal, tableStyles } from '@/shared/ui';

/**
 * 편집영역 템플릿 — 카테고리마다 기본 편집영역(면·표면·인쇄 영역 mm·실측 종류·기본 폰트/잉크/인쇄 방식)을 둔다.
 * 같은 카테고리의 새 상품은 편집영역 화면에서 [카테고리 템플릿 적용] 후 색상별 목업 사진만 올리면 된다.
 */
export const EditAreaTemplateMasterSection = (): ReactElement => {
  const templatesQuery = useEditAreaTemplatesQuery();
  const invalidate = useInvalidateEditAreaTemplates();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<EditAreaTemplate | null>(null);

  const updateAction = useUpdateEditAreaTemplate({
    onSuccess: () => {
      void invalidate();
      setRenameTarget(null);
    },
  });
  const deleteAction = useDeleteEditAreaTemplate({ onSuccess: () => void invalidate() });
  const mutationError = updateAction.error ?? deleteAction.error;

  const handleDelete = (template: EditAreaTemplate): void => {
    if (
      window.confirm(
        `템플릿 "${template.name}" 을(를) 삭제하시겠습니까? 이미 적용해 저장한 상품의 편집영역은 그대로 남습니다.`,
      )
    )
      deleteAction.deleteTemplate({ id: template.id });
  };

  return (
    <ContentCard
      title="편집영역 템플릿"
      description="카테고리별 기본 편집영역. 대분류에 지정하면 소분류 전체가 쓰고, 소분류 템플릿이 우선합니다."
      actions={
        <Button variant="outline" size="sm" onClick={() => setIsCreateOpen(true)}>
          + 상품 설정으로 템플릿 만들기
        </Button>
      }
    >
      {mutationError ? (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {mutationError.message}
        </p>
      ) : null}

      {templatesQuery.isPending ? (
        <EmptyState>불러오는 중…</EmptyState>
      ) : templatesQuery.isError ? (
        <EmptyState isError>
          템플릿을 불러오지 못했습니다: {templatesQuery.error.message}
        </EmptyState>
      ) : templatesQuery.data.length === 0 ? (
        <EmptyState>
          등록된 템플릿이 없습니다. 편집영역을 다 잡은 상품 하나로 템플릿을 만들어 보세요.
        </EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-foreground/[0.06]">
          <table className={tableStyles.table}>
            <thead className={tableStyles.head}>
              <tr>
                <th className={tableStyles.th}>템플릿</th>
                <th className={cn(tableStyles.th, 'w-64')}>카테고리</th>
                <th className={tableStyles.th}>담긴 내용</th>
                <th className={cn(tableStyles.th, 'w-48')}>관리</th>
              </tr>
            </thead>
            <tbody>
              {templatesQuery.data.map((template) => (
                <EditAreaTemplateRow
                  key={template.id}
                  template={template}
                  isPending={updateAction.isPending || deleteAction.isPending}
                  onCategoryChange={(target, categoryId) =>
                    updateAction.updateTemplate({ id: target.id, name: target.name, categoryId })
                  }
                  onRename={setRenameTarget}
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-muted-foreground">
        ⓘ 템플릿에는 목업 사진·원통 마스크가 없습니다. 서피스는 사진 % 좌표라 원본 상품과 같은 표준
        구도(제품 크기·위치)로 찍은 목업에서 그대로 맞습니다. 실측이 다르면 인쇄 영역을 비율로
        맞춥니다.
      </p>

      {isCreateOpen ? (
        <TemplateFromProductModal
          onCreated={() => {
            void invalidate();
            setIsCreateOpen(false);
          }}
          onClose={() => setIsCreateOpen(false)}
        />
      ) : null}
      {renameTarget !== null ? (
        <NameFormModal
          title="템플릿 이름 변경"
          label="템플릿 이름"
          placeholder="예: 반팔 티셔츠"
          initialName={renameTarget.name}
          maxLength={MAX_EDIT_AREA_TEMPLATE_NAME_LENGTH}
          isPending={updateAction.isPending}
          errorMessage={updateAction.error?.message ?? null}
          onSubmit={(name) =>
            updateAction.updateTemplate({
              id: renameTarget.id,
              name,
              categoryId: renameTarget.categoryId,
            })
          }
          onClose={() => setRenameTarget(null)}
        />
      ) : null}
    </ContentCard>
  );
};
